export const PRESENT_CATALOGUE_RESULTS_NODE_TYPE = "present_catalogue_results";
export const PRESENT_CATALOGUE_RESULTS_DEFAULT_PROMPT =
  "Please select an option.";
export const PRESENT_CATALOGUE_RESULTS_MAXIMUM_PROMPT_LENGTH = 512;
export const PRESENT_CATALOGUE_SECTION_MODES = Object.freeze([
  { value: "flat", label: "Flat" },
  { value: "category", label: "Group by catalogue category" },
]);
export const PRESENT_CATALOGUE_COMMERCE_DEFAULT_QUANTITY_PROMPT =
  "How many do you need?";
export const PRESENT_CATALOGUE_COMMERCE_MAXIMUM_QUANTITY_PROMPT_LENGTH = 1024;
export const PRESENT_CATALOGUE_COMMERCE_TRANSACTION_TYPES = Object.freeze([
  { value: "order", label: "Order" },
  { value: "enquiry", label: "Enquiry" },
  { value: "quotation_request", label: "Quotation Request" },
  { value: "booking", label: "Booking" },
  { value: "service_request", label: "Service Request" },
]);
export const PRESENT_CATALOGUE_COMMERCE_QUANTITY_MODES = Object.freeze([
  { value: "ask_customer", label: "Ask customer" },
  { value: "fixed_one", label: "Fixed one" },
]);
export const PRESENT_CATALOGUE_RESULTS_PATHS = Object.freeze([
  { id: "selected", label: "Selected" },
  { id: "unavailable", label: "Unavailable" },
]);

export const createPresentCatalogueResultsConfig = () => ({
  searchNodeId: "",
  promptText: PRESENT_CATALOGUE_RESULTS_DEFAULT_PROMPT,
  sectionMode: "flat",
});

export const createPresentCatalogueCommerceConfig = () => ({
  transactionType: "enquiry",
  quantityMode: "ask_customer",
  quantityPrompt: PRESENT_CATALOGUE_COMMERCE_DEFAULT_QUANTITY_PROMPT,
});

export const normalizePresentCatalogueCommerceConfig = commerce => {
  if (!commerce || typeof commerce !== "object" || Array.isArray(commerce)) {
    return null;
  }

  const transactionType = String(commerce.transactionType || "")
    .trim()
    .toLowerCase();
  const quantityMode = String(commerce.quantityMode || "")
    .trim()
    .toLowerCase();
  const normalized = { transactionType, quantityMode };

  if (quantityMode === "ask_customer") {
    normalized.quantityPrompt = String(commerce.quantityPrompt || "").trim();
  }

  return normalized;
};

export const validatePresentCatalogueCommerceConfig = commerce => {
  if (commerce == null) {
    return { isValid: true, message: "", normalized: null };
  }

  const normalized = normalizePresentCatalogueCommerceConfig(commerce);
  if (!normalized) {
    return {
      isValid: false,
      message: "Commerce settings are invalid.",
      reason: "invalid_commerce",
    };
  }

  if (
    !PRESENT_CATALOGUE_COMMERCE_TRANSACTION_TYPES.some(
      option => option.value === normalized.transactionType
    )
  ) {
    return {
      isValid: false,
      message: "Select a transaction type.",
      reason: "invalid_transaction_type",
    };
  }

  if (
    !PRESENT_CATALOGUE_COMMERCE_QUANTITY_MODES.some(
      option => option.value === normalized.quantityMode
    )
  ) {
    return {
      isValid: false,
      message: "Select how quantity should be collected.",
      reason: "invalid_quantity_mode",
    };
  }

  if (normalized.quantityMode === "ask_customer") {
    if (!normalized.quantityPrompt) {
      return {
        isValid: false,
        message: "Enter a quantity question.",
        reason: "quantity_prompt_required",
      };
    }
    if (
      normalized.quantityPrompt.length >
      PRESENT_CATALOGUE_COMMERCE_MAXIMUM_QUANTITY_PROMPT_LENGTH
    ) {
      return {
        isValid: false,
        message: `Quantity question cannot exceed ${PRESENT_CATALOGUE_COMMERCE_MAXIMUM_QUANTITY_PROMPT_LENGTH} characters.`,
        reason: "quantity_prompt_too_long",
      };
    }
  }

  return { isValid: true, message: "", normalized };
};

export const getSearchCatalogueNodes = nodes =>
  (Array.isArray(nodes) ? nodes : [])
    .filter(
      node =>
        String(node?.type || "").trim().toLowerCase() === "search_catalogue"
    )
    .map(node => {
      const config = node?.data?.config || {};
      return {
        id: String(node?.id || "").trim(),
        label: String(
          node?.data?.label || node?.data?.nodeName || "Search Catalogue"
        ).trim(),
        queryFieldKey: String(config.queryFieldKey || "").trim(),
        searchMode: String(config.searchMode || "filtered").trim().toLowerCase(),
        catalogId: String(config.catalogId || "").trim() || null,
      };
    })
    .filter(node => node.id);

export const findSearchCatalogueNode = (nodes, searchNodeId) => {
  const normalizedId = String(searchNodeId || "").trim().toLowerCase();
  if (!normalizedId) return null;
  return (
    getSearchCatalogueNodes(nodes).find(
      node => node.id.toLowerCase() === normalizedId
    ) || null
  );
};

export const validatePresentCatalogueResultsConfig = (config, nodes) => {
  const searchNodeId = String(config?.searchNodeId || "").trim();
  const promptText = String(config?.promptText ?? "").trim();
  const sectionMode = String(config?.sectionMode || "flat").trim().toLowerCase();

  if (!searchNodeId) {
    return {
      isValid: false,
      message: "Select a Search Catalogue node from this flow.",
      reason: "missing_search_node",
    };
  }

  if (Array.isArray(nodes)) {
    const searchNodeExists = !!findSearchCatalogueNode(nodes, searchNodeId);
    if (!searchNodeExists) {
      return {
        isValid: false,
        message:
          "The selected Search Catalogue node is not available in this flow.",
        reason: "stale_search_node",
      };
    }
  }

  if (promptText.length > PRESENT_CATALOGUE_RESULTS_MAXIMUM_PROMPT_LENGTH) {
    return {
      isValid: false,
      message: `Prompt cannot exceed ${PRESENT_CATALOGUE_RESULTS_MAXIMUM_PROMPT_LENGTH} characters.`,
      reason: "prompt_too_long",
    };
  }
  if (!["flat", "category"].includes(sectionMode)) {
    return {
      isValid: false,
      message: "Select a valid native product grouping.",
      reason: "invalid_section_mode",
    };
  }

  const commerceValidation = validatePresentCatalogueCommerceConfig(
    config?.commerce
  );
  if (!commerceValidation.isValid) {
    return commerceValidation;
  }

  return {
    isValid: true,
    message: "",
    normalized: {
      searchNodeId,
      promptText:
        promptText || PRESENT_CATALOGUE_RESULTS_DEFAULT_PROMPT,
      sectionMode,
      ...(commerceValidation.normalized
        ? { commerce: commerceValidation.normalized }
        : {}),
    },
  };
};
